import { ClaimDataPaidStatus } from '@truepill/tpos-types'
import type { Claim, Fill } from 'types'
import { calculateClaimPricingResponseFee } from 'utils'

export const getInsuranceKey = (bin: string, cardholderId: string, pcn?: string): string =>
  `${bin}:${pcn || ''}:${cardholderId}`

export const getRunIdFromFill = (claims: Claim[]): string | undefined => {
  // if there was a paid claim, grab it (there is a chance that we discard a rejected secondary that would be after the primary)
  const lastPaidClaim = claims.filter(claim => ClaimDataPaidStatus.includes(claim.status || '')).pop()
  // otherwise, fall back to the last claim (we stop at a failed claim so, if there was no paid claim, we can safely grab the last one)
  const lastClaim = lastPaidClaim ?? claims[claims.length - 1]
  // grab the last run id
  const lastRunIdIndex = (lastClaim?.runIds?.length ?? 1) - 1
  return lastClaim?.runIds?.[lastRunIdIndex]
}

export const getClaimsFromLastRun = (claims: Claim[], runId: string | undefined): Claim[] => {
  const lastRunId = runId ?? getRunIdFromFill(claims)

  if (!lastRunId) {
    return []
  }

  const claimsFromLastRun = claims.filter(claim => claim.runIds?.includes(lastRunId))

  return claimsFromLastRun
}

export const calculateProfit = (fill?: Fill, claims?: Claim[]): number | undefined => {
  if (!fill || !claims?.length) {
    return undefined
  }

  const acquisitionCost = claims.find(c => c?.cost !== undefined)?.cost

  const paidClaims = claims.filter(claim => {
    const status = claim?.received?.groups?.[0]?.response?.transactionStatus
    const pricing = claim?.received?.groups?.[0]?.pricingResponse
    return !!pricing && status !== 'R'
  })

  if (paidClaims.length === 0 || acquisitionCost === undefined) {
    return undefined
  }

  const totals = paidClaims.reduce(
    (acc, claim) => {
      const pricing = claim?.received?.groups?.[0]?.pricingResponse
      const feeForClaim = pricing ? calculateClaimPricingResponseFee(pricing) : 0
      acc.remittance += pricing?.totalAmountPaid ?? 0
      acc.copay += pricing?.patientPayAmount ?? 0
      acc.fee += feeForClaim ?? 0
      return acc
    },
    { remittance: 0, copay: 0, fee: 0 },
  )

  return totals.remittance + totals.copay + totals.fee - acquisitionCost
}
